import CollectionDetailDescription from '@/components/elements/doccen-lib/collection-detail/CollectionDetailDescription';
import CollectionDetailMoreInformation from '@/components/elements/doccen-lib/collection-detail/CollectionDetailMoreInformation';
import DocumentList from '@/components/elements/doccen-lib/collection-detail/DocumentList';
import { LIBRARY_COLLECTION_DETAIL } from '@/constant/QueryKeyName';
import { generateQuery } from '@/helpers/pathName';
import { ICollection } from '@/interfaces/doccen-lib/collection.interface';
import { getDoccenCollection } from 'apis/tms/library/collection.api';
import { AxiosError } from 'axios';
import { PATH_NAME } from 'configs/pathName';
import { Avatar } from 'design-system/components/Avatar';
import { BreadcrumbItem } from 'design-system/components/Breadcrumb';
import { Header } from 'design-system/components/Header';
import { TabDataProps, Tabs } from 'design-system/components/Tabs';
import { Typography } from 'design-system/components/Typography';
import BaseLayout from 'layouts';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { NextPageWithLayout } from '../../page';

const tabs: TabDataProps[] = [
  {
    title: 'Mô tả',
    tabId: 'overview',
    href: '#overview-section',
  },
  {
    title: 'Danh sách nội dung',
    tabId: 'documents',
    href: '#documents-section',
  },
];

const CollectionPage: NextPageWithLayout<{}> = () => {
  const router = useRouter();
  const { cid: collectionId } = router.query;

  const { data: collection } = useQuery<ICollection, AxiosError, ICollection>(
    [LIBRARY_COLLECTION_DETAIL, collectionId],
    () => getDoccenCollection(collectionId as string),
    {
      initialData: {} as ICollection,
      enabled: !!collectionId,
      onError(err) {
        if (err.response?.status === 401) {
          router.push({
            pathname: PATH_NAME.LOGIN,
            query: { ...generateQuery(router) },
          });
          return;
        }

        if (err.response?.status === 403) {
          router.push({
            pathname: PATH_NAME.FORBIDDEN,
          });
          return;
        }
      },
    }
  );

  const isOwner = collection?.isOwner;

  const [currentTab, setCurrentTab] = useState<number>(0);

  const hashContent = {
    overview: (
      <>
        <Typography variant="heading2" className="mb-3">
          Mô tả
        </Typography>
        <CollectionDetailDescription
          description={collection?.description || 'Chưa có mô tả'}
        />
      </>
    ),
    documents: <DocumentList collection={collection!} />,
  };
  const breadcrumb: BreadcrumbItem[] = useMemo(() => {
    const baseBreadcrumb = [
      {
        name: 'Thư viện Doccen',
        path: PATH_NAME.LIBRARY,
        className: 'line-clamp-none whitespace-nowrap',
      },
      {
        name: collection?.name || '',
        path: '#',
        className: 'line-clamp-none whitespace-nowrap',
      },
    ];

    if (isOwner) {
      baseBreadcrumb.splice(1, 0, {
        name: 'Quản lý nội dung',
        path: PATH_NAME.LIBRARY_CONTENT_MANAGEMENT,
        className: 'line-clamp-none whitespace-nowrap',
      });
    }

    return baseBreadcrumb;
  }, [collection, isOwner]);

  return (
    <div className="flex w-full flex-col items-center">
      <Header
        title=""
        breadcrumb={{
          breadcrumbItems: breadcrumb,
          startPlace: {
            iconName: 'library',
            title: 'Thư viện Doccen',
          },
        }}
        className="!gap-0 px-3 py-2 md:px-6 md:py-4"
      />
      <div className="flex w-full max-w-container gap-4 px-0 md:px-5">
        <div className="flex h-full min-h-dvh flex-1 flex-col bg-white p-4 pb-24 text-left md:p-6">
          <div className="flex flex-col gap-2">
            <Typography variant="display2">
              {collection?.name || 'Tên bộ sưu tập'}
            </Typography>
            <div className="my-4 flex items-center gap-2">
              <Link href={collection?.owner?.profileUrl || ''} passHref>
                <Avatar
                  type="image"
                  src={collection?.owner?.avatar}
                  size={24}
                  className="cursor-pointer"
                />
              </Link>
              <div>
                <Typography
                  variant="input-label"
                  className="text-Text/LightBackground/Secondary"
                >
                  Được tạo bởi:
                </Typography>

                <Link href={collection?.owner?.profileUrl || ''} passHref>
                  <a className="hover:underline">
                    <Typography
                      variant="body3"
                      className="line-clamp-2 flex items-center gap-1"
                    >
                      {collection?.owner?.academicRank &&
                        `${collection?.owner?.academicRank}. `}
                      {collection?.owner?.name}
                    </Typography>
                  </a>
                </Link>
              </div>
            </div>
            <img src={collection?.thumbnailUrl} className="aspect-video" />
          </div>

          <Tabs
            tabs={tabs}
            selected={currentTab}
            onSelect={(_, index) => setCurrentTab(index)}
            className="sticky left-0 top-[50px] z-[9] mb-6 w-full max-w-[100dvw] bg-white"
            sx={{
              '& .MuiTabs-flexContainer': {
                borderBottom: '2px solid #E5E5E5',
              },
            }}
          />

          <div className="flex flex-col gap-6">
            {tabs.map((tab) => (
              <div
                key={tab.tabId}
                id={tab.href?.replace('#', '')}
                className="scroll-mt-28"
              >
                {hashContent[tab.tabId as keyof typeof hashContent]}
              </div>
            ))}
          </div>
        </div>

        <CollectionDetailMoreInformation collection={collection} />
      </div>
    </div>
  );
};

export default CollectionPage;

CollectionPage.getLayout = (page) => {
  return <BaseLayout>{page}</BaseLayout>;
};
